/**
 * Per-entry content keys, which are what let a child read their own rows.
 *
 * THE PROBLEM. A transaction reason is free text a parent typed, and it
 * routinely contains a child's name -- "Susan tidied up". It is encrypted, so
 * the server cannot read it. But a child's tablet holds a device token scoped
 * to one person and deliberately does NOT hold the family key: handing it over
 * would undo the separation the two Lambda authorizers exist to enforce. So a
 * child could see their own balance and not a word of why it changed.
 *
 * THE SHAPE, which DATA-MODEL.md anticipated and the API already carries:
 *
 *   reason  --sealed under-->  content key (random, one per entry)
 *                                  |
 *                                  +-- wrapped for the family  -> reasonKeys.fam
 *                                  +-- wrapped for each person -> reasonKeys[pid]
 *
 * The server copies each person's wrapping onto that person's own share row as
 * `rkey`, so a child-scoped query returns exactly one key: theirs. It cannot
 * return a sibling's, because the query is a begins_with on their own person
 * id and the sibling's wrapping is on a row that query cannot reach.
 *
 * A PERSON KEY is the long-lived key each of those wrappings is made with. It
 * is stored once, sealed under the family key, as `pfw` on the person -- so a
 * parent can always get it -- and handed to the child's device inside the link
 * code when that device is linked.
 *
 * BACKWARD COMPATIBILITY is not optional here. Every entry and every person in
 * an existing family was written before this scheme existed: the reason is
 * sealed directly under the family key and there are no wrappings at all. Both
 * read paths below fall back to that, so old rows keep working and new ones
 * gain the child's copy. Nothing is migrated and nothing needs to be.
 */

import { decryptField, encryptField, fromBase64, toBase64 } from './familyKey';

/** The family's own wrapping. The one holder that is not a person. */
export const FAMILY = 'fam';

async function randomKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, [
    'encrypt',
    'decrypt',
  ]);
}

async function raw(key: CryptoKey): Promise<string> {
  return toBase64(await crypto.subtle.exportKey('raw', key));
}

async function fromRaw(bytes: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'raw',
    fromBase64(bytes) as BufferSource,
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt'],
  );
}

/** A fresh key for one person. Long-lived; one per person, not per entry. */
export async function createPersonKey(): Promise<CryptoKey> {
  return randomKey();
}

/** Seal a person key under the family key, for storage as `pfw`. */
export async function sealPersonKey(familyKey: CryptoKey, person: CryptoKey): Promise<string> {
  return encryptField(familyKey, await raw(person));
}

export async function openPersonKey(familyKey: CryptoKey, sealed: string): Promise<CryptoKey> {
  return fromRaw(await decryptField(familyKey, sealed));
}

export interface SealedReason {
  reasonCt: string;
  /** Wrappings of this entry's content key, by holder. */
  reasonKeys: Record<string, string>;
}

/**
 * Seal a reason for the family and for each person named on the row.
 *
 * A fresh content key per entry, rather than one key per person reused: a
 * person key that leaked would otherwise open every reason that person was
 * ever named on. Here it opens the wrappings, and each wrapping opens exactly
 * one entry.
 */
export async function sealReason(
  familyKey: CryptoKey,
  personKeys: Map<string, CryptoKey>,
  reason: string,
): Promise<SealedReason> {
  const content = await randomKey();
  const exported = await raw(content);

  const reasonKeys: Record<string, string> = {
    [FAMILY]: await encryptField(familyKey, exported),
  };
  for (const [personId, personKey] of personKeys) {
    reasonKeys[personId] = await encryptField(personKey, exported);
  }

  return { reasonCt: await encryptField(content, reason), reasonKeys };
}

/**
 * Open a reason, whichever scheme it was written under.
 *
 * `wrapped` is this holder's copy of the content key -- `reasonKeys.fam` for a
 * parent, `rkey` for a child. Absent means the entry predates the scheme and
 * the reason is sealed directly under the opener, which for a parent is the
 * family key and for a child is nothing they can read.
 *
 * Returns empty rather than throwing. One unreadable reason must not take down
 * a journal, and a child looking at a row written before their device existed
 * should see the amount and the date, not an error.
 */
export async function openReason(
  opener: CryptoKey | null,
  reasonCt?: string | null,
  wrapped?: string | null,
): Promise<string> {
  if (!reasonCt || !opener) return '';
  try {
    if (!wrapped) return await decryptField(opener, reasonCt);
    const content = await fromRaw(await decryptField(opener, wrapped));
    return await decryptField(content, reasonCt);
  } catch {
    return '';
  }
}
